import { EstateWaterfall_NodeFragment } from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import {
  EstateWaterfallGraph,
  GraphNodeCategorizationType,
} from '@/modules/estateWaterfall/types';
import { parseNodeId } from '@/modules/estateWaterfall/utils/nodeId.utils';
import { AfterDeath, EntityKind, TileNotesInput } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  entityNodeToTileNotesInput,
  isSupportedTileNotesEntityKind,
} from './types';

export interface TileEntityInfo {
  tileId: string;
  entityKind: EntityKind | null;
  isSupported: boolean;
}

function getEntityKindFromNode(
  node: EstateWaterfall_NodeFragment['node']
): EntityKind | null {
  if (node.__typename === 'Entity') {
    return node.entityKind;
  }
  return null;
}

function isEntitySupported(
  node: EstateWaterfall_NodeFragment['node']
): boolean {
  if (node.__typename === 'TestamentaryEntity') {
    return true;
  }
  if (node.__typename === 'Entity') {
    return isSupportedTileNotesEntityKind(node.entityKind);
  }
  return false;
}

export function getTileEntityInfo(
  tileId: string,
  waterfallGraph: EstateWaterfallGraph
): TileEntityInfo | null {
  try {
    const nodeAttrs = waterfallGraph.getNodeAttributes(tileId);
    if (!nodeAttrs?.data) return null;

    if (
      nodeAttrs.categorizationType === GraphNodeCategorizationType.GroupNode
    ) {
      return getGroupTileEntityInfo(tileId, waterfallGraph);
    }

    const data = nodeAttrs.data as EstateWaterfall_NodeFragment;
    const entityKind = getEntityKindFromNode(data.node);
    const isSupported = isEntitySupported(data.node);

    return {
      tileId,
      entityKind,
      isSupported,
    };
  } catch {
    return null;
  }
}

function getGroupTileEntityInfo(
  tileId: string,
  waterfallGraph: EstateWaterfallGraph
): TileEntityInfo | null {
  const waterfall = waterfallGraph.getAttribute('waterfall');
  if (!waterfall) return null;

  const nodeAttrs = waterfallGraph.getNodeAttributes(tileId);
  if (!nodeAttrs?.data) return null;

  const { afterDeath } = parseNodeId(tileId);
  const groupId = nodeAttrs.data.id;

  const vizGroups = getNodes(waterfall.visualizationGroups);
  const group = vizGroups.find(
    (g) => g.id === groupId && g.namespace === afterDeath
  );
  if (!group) return null;

  const memberNodes = waterfall.visualizationWithProjections.nodes.filter(
    (vn) => vn.afterDeath === afterDeath && vn.group?.id === groupId
  );

  const hasAnySupported = memberNodes.some((vn) => isEntitySupported(vn.node));
  const hasAnyUnsupported = memberNodes.some(
    (vn) => !isEntitySupported(vn.node)
  );

  return {
    tileId,
    entityKind: null,
    isSupported: hasAnySupported && !hasAnyUnsupported,
  };
}

export function getUnsupportedTileIds(
  tileIds: string[],
  waterfallGraph: EstateWaterfallGraph
): string[] {
  return tileIds.filter((tileId) => {
    const info = getTileEntityInfo(tileId, waterfallGraph);
    return info && !info.isSupported;
  });
}

export function buildTileNotesInput(
  tileIds: string[],
  waterfallGraph: EstateWaterfallGraph
): TileNotesInput[] {
  const inputs: TileNotesInput[] = [];

  for (const tileId of tileIds) {
    try {
      const nodeAttrs = waterfallGraph.getNodeAttributes(tileId);
      if (!nodeAttrs?.data) continue;

      const { afterDeath } = parseNodeId(tileId);

      // Handle group nodes
      if (
        nodeAttrs.categorizationType === GraphNodeCategorizationType.GroupNode
      ) {
        const groupInput = buildGroupTileNotesInput(
          tileId,
          afterDeath,
          waterfallGraph
        );
        if (groupInput) {
          inputs.push(groupInput);
        }
        continue;
      }

      // Handle single entity/testamentary entity nodes
      const data = nodeAttrs.data as EstateWaterfall_NodeFragment;

      const input = entityNodeToTileNotesInput(data.node, tileId, afterDeath);
      if (input) {
        inputs.push(input);
      }
    } catch {
      continue;
    }
  }

  return inputs;
}

export interface BuildGroupTileNotesInputParams {
  tileIds: string[];
  namespace: AfterDeath;
  waterfallGraph: EstateWaterfallGraph;
  groupDisplayName?: string;
  groupDescription?: string;
}

export function buildGroupTileNotesInputFromMembers({
  tileIds,
  namespace,
  waterfallGraph,
  groupDisplayName,
  groupDescription,
}: BuildGroupTileNotesInputParams): TileNotesInput | null {
  const entityIDs: string[] = [];
  const testamentaryEntityIDs: string[] = [];

  for (const tileId of tileIds) {
    try {
      const prefixedTileId = `${namespace}:${tileId}`;
      const nodeAttrs = waterfallGraph.getNodeAttributes(prefixedTileId);
      if (!nodeAttrs?.data) continue;

      const data = nodeAttrs.data as EstateWaterfall_NodeFragment;

      if (data.node.__typename === 'Entity') {
        if (isSupportedTileNotesEntityKind(data.node.entityKind)) {
          entityIDs.push(data.node.id);
        }
      } else if (data.node.__typename === 'TestamentaryEntity') {
        testamentaryEntityIDs.push(data.node.id);
      }
    } catch {
      continue;
    }
  }

  if (entityIDs.length === 0 && testamentaryEntityIDs.length === 0) {
    return null;
  }

  return {
    tileID: `group-${namespace}`,
    deathSection: namespace,
    entityIDs: entityIDs.length > 0 ? entityIDs : undefined,
    testamentaryEntityIDs:
      testamentaryEntityIDs.length > 0 ? testamentaryEntityIDs : undefined,
    groupDisplayName: groupDisplayName || undefined,
    groupDescription: groupDescription || undefined,
  };
}

function buildGroupTileNotesInput(
  tileId: string,
  afterDeath: AfterDeath,
  waterfallGraph: EstateWaterfallGraph
): TileNotesInput | null {
  const waterfall = waterfallGraph.getAttribute('waterfall');
  if (!waterfall) return null;

  const nodeAttrs = waterfallGraph.getNodeAttributes(tileId);
  if (!nodeAttrs?.data) return null;

  const groupId = nodeAttrs.data.id;

  // Find the GraphVizGroup (for displayName/description)
  const vizGroups = getNodes(waterfall.visualizationGroups);
  const group = vizGroups.find(
    (g) => g.id === groupId && g.namespace === afterDeath
  );
  if (!group) return null;

  // Find member viz nodes for this group + section
  // Note: group.nodes only has { id }, __typename comes from visualizationWithProjections
  const memberNodes = waterfall.visualizationWithProjections.nodes.filter(
    (vn) => vn.afterDeath === afterDeath && vn.group?.id === groupId
  );

  const entityIDs = memberNodes
    .filter(
      (vn) =>
        vn.node.__typename === 'Entity' &&
        isSupportedTileNotesEntityKind(vn.node.entityKind)
    )
    .map((vn) => vn.node.id);

  const testamentaryEntityIDs = memberNodes
    .filter((vn) => vn.node.__typename === 'TestamentaryEntity')
    .map((vn) => vn.node.id);

  if (entityIDs.length === 0 && testamentaryEntityIDs.length === 0) {
    return null;
  }

  return {
    tileID: tileId,
    deathSection: afterDeath,
    entityIDs: entityIDs.length > 0 ? entityIDs : undefined,
    testamentaryEntityIDs:
      testamentaryEntityIDs.length > 0 ? testamentaryEntityIDs : undefined,
    groupDisplayName: group.displayName ?? undefined,
    groupDescription: group.description ?? undefined,
  };
}
